/**
 * Shared options and defaults for the "Generate data with AI" modal flows.
 *
 * Chip / segmented values mirror the Figma frames; the segmented ids are the
 * backend wire values (`GenerationTextLength` / `GenerationContentType` in
 * optune-utils `generation_event.py`).
 */

export const LANGUAGE_OPTIONS = [
    'English',
    'Spanish',
    'French',
    'German',
    'Portuguese',
    'Arabic',
    'Chinese',
    'Japanese',
    'Hindi',
];

export const TONE_STYLE_OPTIONS = [
    'Slang',
    'Professional',
    'Emojis',
    'Conversational',
    'Academic',
    'Formal',
    'Casual',
];

export const TEXT_LENGTH_ITEMS = [
    { id: 'short', label: 'Short' },
    { id: 'medium', label: 'Medium' },
    { id: 'long', label: 'Long' },
];

/** Per-option help copy for classification / NER Text length segments. */
export const TEXT_LENGTH_OPTION_HELP = {
    short: 'roughly a sentence or two',
    medium: 'roughly a paragraph',
    long: 'multiple paragraphs',
};

export const CONTENT_TYPE_ITEMS = [
    { id: 'text_only', label: 'Text Only' },
    { id: 'text_and_code', label: 'Text & Code' },
    { id: 'code_only', label: 'Code only' },
];

export const DOMAIN_OPTIONS = [
    'Customer Support',
    'E-commerce',
    'Finance',
    'Healthcare',
    'Legal',
    'Technology',
    'Education',
    'Travel & Hospitality',
    'Media & Entertainment',
    'Other',
];

/** Figma default for the single Total Records input (summarization / content generation). */
export const DEFAULT_TOTAL_RECORDS = 1000;

/** Backend `dataset_size` constraint is `ge=10`. */
export const MIN_TOTAL_RECORDS = 10;

/** Backend `dataset_size` / summed `record_counts` ceiling (`optuneutils.constants.record_limits`). */
export const MAX_TOTAL_RECORDS = 10_000;

export const HELP_TEXTS = {
    generationGoal: 'What should this dataset help the model do?',
    datasetContext:
        'Describe the data you need, including the type of input, use case, and any important context.',
    totalRecords: 'Total number of records to generate across all targets. Minimum 10, maximum 10,000.',
    recordCount: 'How many records to generate for this target. All targets together: minimum 10, maximum 10,000.',
    labelDescription: 'When should this label apply? Guides the generator toward unambiguous samples.',
    entityDescription: 'What does this entity type cover? Guides the generator toward clear entity mentions.',
    examplesPrompt: 'Example texts that belong to this label. Generation uses them as reference samples.',
    examples: 'Add an example text the model should learn to summarize.',
    contentGenerationExample: 'Add an example input the model should learn to generate from.',
    exampleSummary: 'The reference summary for this example. Generation uses it as a style and quality guide.',
    exampleOutput: 'The reference output for this example. Generation uses it as a style and quality guide.',
    exampleLabels:
        'Labels that apply to this example. Leave empty for No predictions (none of the labels apply).',
    nerExamples:
        'Add an example and tag its entities to generate similar examples and entity extractions..',
    exclude: 'Describe examples that should not be included.',
    textLength: 'Approximate length of each generated text.',
    language: 'Languages the generated content should be written in.',
    toneStyle: 'Tone and writing style of the generated content.',
    domain: 'Business domain the generated content should belong to.',
    contentType: 'Whether generated samples are plain text, text with code, or code only.',
    customGuidelines: 'Free-form instructions the generator must follow (terminology, formatting, restrictions).',
    advancedSettings: 'Fine-tune length, language, and tone for this section.',
};

export const NER_EXAMPLES_INSTRUCTION =
    'Highlight one or more words and select an entity type to label them';

/** "Load Previous ... Configurations" dropdown label on the Define Task step. */
export const LOAD_PREVIOUS_GENERATION_LABEL = 'Load Previous Generation Configurations';

/** Third-party charges warning shown on the final Review step of both the
 * Generate-with-AI and Retrieval Agent flows. */
export const COST_BANNER_TEXT =
    'This process uses third-party AI providers and may generate additional charges billed directly to your ' +
    'connected provider account. Costs are affected by: selected model provider and dataset size.';

/** "Generation Goal" placeholder shared by both Define Task steps. */
export const GENERATION_GOAL_PLACEHOLDER = 'e.g., Classify customer feedback by sentiment';

/** "Description" placeholder shared by both Define Task steps. */
export const CUSTOM_GUIDELINES_PLACEHOLDER =
    'Describe context, follow our support terminology. Avoid competitor names. Keep responses suitable for a ' +
    'professional support dataset. e.g. Generate realistic customer feedback about online purchases, including ' +
    'delivery, product quality, and support experiences.';

/** Define Task info banner when the task has no existing records. */
export const EMPTY_DATASET_BANNER_TEXT =
    'Start with AI-generated data. Generate examples and labels to build your dataset.';

export const EMPTY_NER_DATASET_BANNER_TEXT =
    'Start with AI-generated data. Generate examples and entities to build your dataset.';

export const EMPTY_SUMMARIZATION_DATASET_BANNER_TEXT =
    'Start with AI-generated data. Generate examples and summaries to build your dataset.';

export const EMPTY_CONTENT_GENERATION_DATASET_BANNER_TEXT =
    'Start with AI-generated data. Generate examples and outputs to build your dataset.';

export const SUMMARIZATION_EXAMPLE_PLACEHOLDER =
    'e.g., Paste a long article or passage the model should summarize.';
export const SUMMARIZATION_SUMMARY_PLACEHOLDER = 'e.g., A concise summary of the example.';
export const CONTENT_GENERATION_EXAMPLE_PLACEHOLDER =
    'e.g., Paste a prompt or input the model should generate from.';
export const CONTENT_GENERATION_OUTPUT_PLACEHOLDER = 'e.g., The expected generated output for this example.';
